import { FC, useMemo } from 'react'
import moment, { Moment } from 'moment'
import { cardUpcomingPaymentsSelector, currentCCLedgerSelector } from 'components/features/creditCard/creditCardStore/selectors'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { formatUSCurrency } from 'helpers/formatHelpers'
import { BACK_BUTTON_PROPS, BalanceDetailsPaneButton, CardPaymentsPaneButton } from 'components/features/pfm/Button/FloatingPaneButtons'
import FloatingPaneNotification from 'components/features/pfm/FloatingPaneNotification/FloatingPaneNotification'
import { useFlexClassNames } from 'hooks/useClassNames'


const UpcomingPaymentNotification: FC<{}> = () => {
  const widthClassName = useFlexClassNames({ shrinkWidth: true })
  const ledger = useStoreSelector(currentCCLedgerSelector)
  const upcomingPayments = useStoreSelector(cardUpcomingPaymentsSelector)
  const getSuffix = (days: number) => days !== 1 ? ' days' : ' day'
  const getDaysFromNow = (date: Moment | string) => moment().diff(date, 'days') * -1

  const paymentMessages = useMemo(() => {
    if (!upcomingPayments || !ledger) {
      return []
    }
    return upcomingPayments.map((upcomingPayment) => {
      const { amount } = upcomingPayment
      if (upcomingPayment.schedule_type === 'one-time') {
        const daysFromNow = getDaysFromNow(upcomingPayment.payment_date)
        if (daysFromNow < 0) {
          return null
        }
        const daysString = daysFromNow === 0 ? 'for today' : 'in ' + daysFromNow + getSuffix(daysFromNow)
        return 'A ' + formatUSCurrency(amount) + ' payment is scheduled ' + daysString
      }
      const autoPaymentDate = moment(ledger.payment_due_date).subtract(upcomingPayment.days_before_due_date)
      const autoPayDaysFromNow = getDaysFromNow(autoPaymentDate)
      if (autoPayDaysFromNow < 0) {
        return null
      }
      const autoPayDaysString = autoPayDaysFromNow === 0 ? 'for today' : 'in ' + autoPayDaysFromNow + getSuffix(autoPayDaysFromNow)
      return 'An auto-payment is scheduled ' + autoPayDaysString
    })
  }, [ledger, upcomingPayments])

  if (!upcomingPayments || !ledger || !paymentMessages) {
    return null
  }

  const actionChild = (
    <CardPaymentsPaneButton
      text='See Details'
      style='textOnly'
      size='tiny'
      theme='darkCyan'
      backButton={ <BalanceDetailsPaneButton { ...BACK_BUTTON_PROPS } /> }
      className={ widthClassName }
    />
  )

  return (
    <FloatingPaneNotification
      theme='lightCyan'
      style='insight'
      messages={ paymentMessages }
      actionChild={ actionChild }
    />
  )
}

export default UpcomingPaymentNotification
